import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";

import { RetryImage } from "../../../components/RetryImage";

import { useOptionData } from "../../../hook/useOptionData";
import { getModelByID } from "../../../apis/model";
import { useGlobalSnackBar } from "../../SnackbarContext";
import {
	filteredChipOptions,
	getHoverImage,
	Option,
} from "../../../utils/option";
import { Loader } from "../../../components/Loader";

import "../Options/styles.css";

const useStyles = makeStyles(() => ({
	hoveredImageWrapper: {
		position: "absolute",
	},
	chipWrapper: {
		marginRight: "8px",
		cursor: "pointer",
	},
	hoveredImage: {
		width: "200px",
		height: "100px",
		transition: "opacity 0.3s ease-in-out",
		opacity: 1,
		objectFit: "cover",
	},
	hidden: {
		opacity: 0,
	},
}));

export const ModelPreview: React.FC = () => {
	const navigate = useNavigate();
	const classes = useStyles();

	const { ID } = useParams();

	const { options } = useOptionData();

	const { showSnackBar } = useGlobalSnackBar();

	const [model, setModel] = useState<any>(null);
	const [logos, setLogos] = useState<Array<string | ArrayBuffer | null>>([
		null,
		null,
		null,
	]);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [hoveredOption, setHoveredOption] = useState<Option | null>(null);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const handleMouseEnter = (
		option: Option,
		event: React.MouseEvent<HTMLDivElement, MouseEvent>
	) => {
		setMousePosition({ x: event.pageX, y: event.pageY });
		setHoveredOption(option);
	};

	const handleMouseLeave = () => {
		setHoveredOption(null);
	};

	const handleMouseMove = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>
	) => {
		setMousePosition({ x: event.pageX, y: event.pageY });
	};

	useEffect(() => {
		const fetchModelData = async () => {
			try {
				const modelData = await getModelByID({ ID });
				setModel({ ...modelData });
				setLogos(modelData.images ? JSON.parse(modelData.images) : []);

				setLoading(false);
			} catch (error) {
				showSnackBar("Failed to fetch model", "error");
			}
		};

		if (ID) {
			fetchModelData();
		} else {
			setLoading(false);
		}
	}, [ID, showSnackBar]);

	if (isLoading) return <Loader isLoading={isLoading} />;

	return (
		<Box sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}>
			{/* Header */}
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4">
					{model?.model_name}
				</Typography>
				<Typography variant="subtitle1" color="text.secondary">
					Part # {model?.part_number}
				</Typography>
			</Box>

			{/* Images */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Images
				</Typography>
				<Grid container spacing={2}>
					{logos?.map((logo, index) => {
						const imageUrl = typeof logo === "string"
							? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${logo}`
							: undefined;

						return (
							<Grid item xs={12} sm={4} key={index}>
								<RetryImage
									sx={{ width: "100%", height: 200 }}
									variant="square"
									src={imageUrl}
									className="uploadImage"
								/>
							</Grid>
						);
					})}
				</Grid>
			</Paper>

			{/* Pricing */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Pricing
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							MSRP
						</Typography>
						<Typography variant="body1">
							{model?.MSRP != null ? `$${model.MSRP}` : "—"}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							{process.env.REACT_APP_DEALER_NAME} Price
						</Typography>
						<Typography variant="body1">
							{model?.dealer_price != null ? `$${model.dealer_price}` : "—"}
						</Typography>
					</Grid>
				</Grid>
			</Paper>

			{/* Dimensions */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Dimensions
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							Height
						</Typography>
						<Typography variant="body1">
							{model?.demention_height != null ? `${model.demention_height}"` : "—"}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							Width
						</Typography>
						<Typography variant="body1">
							{model?.demention_width != null ? `${model.demention_width}"` : "—"}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							Depth
						</Typography>
						<Typography variant="body1">
							{model?.demention_depth != null ? `${model.demention_depth}"` : "—"}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">
							Rack Spaces
						</Typography>
						<Typography variant="body1">
							{model?.demention_rack_spaces ?? "—"}
						</Typography>
					</Grid>
				</Grid>
			</Paper>

			{/* Description */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Description
				</Typography>
				<Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
					{model?.description || "—"}
				</Typography>
			</Paper>

			{/* Action */}
			<Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
				<Button
					variant="contained"
					onClick={() => navigate(`/models/${ID}/customize`)}
				>
					Add & Customize
				</Button>
			</Box>
		</Box>
	);
};
